import { clusterPinPose } from '../cluster-pin-pose';
import { haversineMeters } from '../distance-meters';
import { normalizeTrackPointFacts } from './normalize-track-point-facts';
import type {
  ConvoyTrackSegmentDraft,
  ConvoyTrackVertex,
  DeviceTrackPoints,
  TrackPoint,
} from './types';

const FORK_VERTEX_MIN_GAP_METERS = 1;

function lerp(a: number, b: number, u: number): number {
  return a + (b - a) * u;
}

/** Nearest track ping by time (for battery / fix / accuracy on interpolated vertices). */
export function nearestTrackPoint(
  points: readonly TrackPoint[],
  t: number,
): TrackPoint | null {
  if (points.length === 0) return null;
  let best = points[0]!;
  let bestDist = Math.abs(best.timestampSec - t);
  for (let i = 1; i < points.length; i++) {
    const p = points[i]!;
    const d = Math.abs(p.timestampSec - t);
    if (d < bestDist) {
      best = p;
      bestDist = d;
    }
  }
  return best;
}

/** Interpolate (or clamp) a track to time t. */
export function interpolateTrackAt(
  points: readonly TrackPoint[],
  t: number,
): { lat: number; lng: number } | null {
  if (points.length === 0) return null;
  if (t <= points[0]!.timestampSec) {
    return { lat: points[0]!.lat, lng: points[0]!.lng };
  }
  const last = points[points.length - 1]!;
  if (t >= last.timestampSec) {
    return { lat: last.lat, lng: last.lng };
  }
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    if (t >= a.timestampSec && t <= b.timestampSec) {
      const dt = b.timestampSec - a.timestampSec;
      const u = dt === 0 ? 0 : (t - a.timestampSec) / dt;
      return { lat: lerp(a.lat, b.lat, u), lng: lerp(a.lng, b.lng, u) };
    }
  }
  return null;
}

function poseToVertex(
  pose: { lat: number; lng: number },
  timestampSec: number,
  factsSource: {
    battery?: number | null;
    fixTypeCode?: number | null;
    accuracyMeters?: number | null;
  } | null,
): ConvoyTrackVertex {
  const facts = normalizeTrackPointFacts(factsSource ?? {});
  return {
    lat: pose.lat,
    lng: pose.lng,
    timestampSec,
    battery: facts.battery,
    fixTypeCode: facts.fixTypeCode,
    accuracyMeters: facts.accuracyMeters,
  };
}

/**
 * Slice member track geometry to [startSec, endSec], including interpolated endpoints
 * with battery / fix / accuracy from the nearest raw ping.
 */
export function sliceTrackVertices(
  track: DeviceTrackPoints | undefined,
  startSec: number,
  endSec: number,
): ConvoyTrackVertex[] {
  if (!track || track.points.length === 0 || endSec < startSec) return [];

  const points = track.points;
  const vertices: ConvoyTrackVertex[] = [];

  const startPose = interpolateTrackAt(points, startSec);
  if (startPose) {
    const nearest = nearestTrackPoint(points, startSec);
    vertices.push(poseToVertex(startPose, startSec, nearest));
  }

  for (const p of points) {
    if (p.timestampSec <= startSec || p.timestampSec >= endSec) continue;
    const prev = vertices[vertices.length - 1];
    if (!prev || prev.lat !== p.lat || prev.lng !== p.lng) {
      const facts = normalizeTrackPointFacts(p);
      vertices.push({
        lat: p.lat,
        lng: p.lng,
        timestampSec: p.timestampSec,
        battery: facts.battery,
        fixTypeCode: facts.fixTypeCode,
        accuracyMeters: facts.accuracyMeters,
      });
    }
  }

  const endPose = interpolateTrackAt(points, endSec);
  if (endPose) {
    const nearest = nearestTrackPoint(points, endSec);
    const v = poseToVertex(endPose, endSec, nearest);
    const prev = vertices[vertices.length - 1];
    if (!prev || prev.lat !== v.lat || prev.lng !== v.lng) {
      vertices.push(v);
    }
  }

  return vertices;
}

/** @deprecated Use {@link sliceTrackVertices}; kept for coordinate-only callers. */
export function sliceTrackCoordinates(
  track: DeviceTrackPoints | undefined,
  startSec: number,
  endSec: number,
): [number, number][] {
  return sliceTrackVertices(track, startSec, endSec).map((v) => [v.lng, v.lat]);
}

/** Member whose pose at `atSec` is the geometric medoid of the group. */
export function pickGeometryDeviceId(
  tracks: readonly DeviceTrackPoints[],
  memberDeviceIds: readonly number[],
  atSec: number,
): number {
  const poses: { deviceId: number; lat: number; lng: number }[] = [];
  for (const id of memberDeviceIds) {
    if (id <= 0) continue;
    const track = tracks.find((t) => t.deviceId === id);
    if (!track) continue;
    const pose = interpolateTrackAt(track.points, atSec);
    if (pose) poses.push({ deviceId: id, ...pose });
  }
  const pin = clusterPinPose(poses);
  if (pin) return pin.deviceId;
  const positive = memberDeviceIds.filter((id) => id > 0);
  return positive.length > 0 ? Math.min(...positive) : 0;
}

export function findParentDraft(
  drafts: readonly ConvoyTrackSegmentDraft[],
  child: ConvoyTrackSegmentDraft,
): ConvoyTrackSegmentDraft | undefined {
  return drafts.find(
    (d) =>
      d.endSec === child.startSec &&
      d.memberDeviceIds.length > child.memberDeviceIds.length &&
      child.memberDeviceIds.every((id) => d.memberDeviceIds.includes(id)),
  );
}

export function forkVertex(
  tracks: readonly DeviceTrackPoints[],
  parentMemberIds: readonly number[],
  atSec: number,
): ConvoyTrackVertex | null {
  const id = pickGeometryDeviceId(tracks, parentMemberIds, atSec);
  const track = tracks.find((t) => t.deviceId === id);
  if (!track) return null;
  const pose = interpolateTrackAt(track.points, atSec);
  if (!pose) return null;
  const nearest = nearestTrackPoint(track.points, atSec);
  return poseToVertex(pose, atSec, nearest);
}

export function forkVertexLngLat(
  tracks: readonly DeviceTrackPoints[],
  parentMemberIds: readonly number[],
  atSec: number,
): [number, number] | null {
  const v = forkVertex(tracks, parentMemberIds, atSec);
  return v ? [v.lng, v.lat] : null;
}

export function prependForkVertexOntoVertices(
  vertices: ConvoyTrackVertex[],
  vertex: ConvoyTrackVertex | null,
): ConvoyTrackVertex[] {
  if (!vertex) return vertices;
  if (vertices.length === 0) return [vertex];
  const first = vertices[0]!;
  if (first.lat === vertex.lat && first.lng === vertex.lng) return vertices;
  const dist = haversineMeters(
    { lat: first.lat, lng: first.lng },
    { lat: vertex.lat, lng: vertex.lng },
  );
  if (dist < FORK_VERTEX_MIN_GAP_METERS) return vertices;
  return [vertex, ...vertices];
}

export function prependForkVertex(
  coords: [number, number][],
  vertex: [number, number] | null,
): [number, number][] {
  if (!vertex) return coords;
  if (coords.length === 0) return [vertex];
  const first = coords[0]!;
  if (first[0] === vertex[0] && first[1] === vertex[1]) return coords;
  const dist = haversineMeters(
    { lat: first[1], lng: first[0] },
    { lat: vertex[1], lng: vertex[0] },
  );
  if (dist < FORK_VERTEX_MIN_GAP_METERS) return coords;
  return [vertex, ...coords];
}

/**
 * Vertices for one membership window: medoid member's track, with fork vertex
 * prepended when this group split from a larger parent.
 */
export function segmentVertices(
  tracks: readonly DeviceTrackPoints[],
  drafts: readonly ConvoyTrackSegmentDraft[],
  draft: ConvoyTrackSegmentDraft,
): ConvoyTrackVertex[] {
  const geomId = pickGeometryDeviceId(
    tracks,
    draft.memberDeviceIds,
    draft.endSec,
  );
  const track = tracks.find((t) => t.deviceId === geomId);
  let vertices = sliceTrackVertices(track, draft.startSec, draft.endSec);
  const parent = findParentDraft(drafts, draft);
  if (parent) {
    vertices = prependForkVertexOntoVertices(
      vertices,
      forkVertex(tracks, parent.memberDeviceIds, draft.startSec),
    );
  }
  return vertices;
}

/**
 * Polyline for one membership window (lng/lat pairs only).
 * Prefer {@link segmentVertices} when payload is needed.
 */
export function segmentCoordinates(
  tracks: readonly DeviceTrackPoints[],
  drafts: readonly ConvoyTrackSegmentDraft[],
  draft: ConvoyTrackSegmentDraft,
): [number, number][] {
  return segmentVertices(tracks, drafts, draft).map((v) => [v.lng, v.lat]);
}
